/**
 * Continuous effects — the layerless model (SPEC.md §4.5, CR §6.6).
 *
 * Lorcana has **no layer system**. Every modifier to a characteristic combines at once, in no
 * order, and the whole thing is recomputed whenever anything changes (CR §6.6). The
 * implementation of that rule is this module plus one discipline: **nothing is cached**. A
 * characteristic read recomputes from the printed base plus whatever is live *right now*, so
 * "recompute on every change" needs no change hooks, no dirty bits and no invalidation — a
 * value cannot go stale if it never exists between reads.
 *
 * ## The two kinds of continuous effect (CR §6.4.2)
 *
 * | | Where it lives | Which cards it affects |
 * |---|---|---|
 * | **Continuous static** — a `{ kind: 'static' }` ability on a card in play | nowhere; read off the card's IR every time | whatever matches its filter **now**, so a character that arrives later is affected immediately |
 * | **Applied static** — what a resolved `modify` / `grant-keyword` effect leaves behind | a `Modifier` record in `GameState.modifiers` | exactly the cards its `scope` **snapshotted** when it resolved; a later arrival is never affected |
 *
 * That difference is the whole point of the record carrying a `scope` rather than a card id:
 * `{ kind: 'cards' }` is a snapshot, `{ kind: 'filter' }` is dynamic, and a continuous static
 * normalises into the second form when it is read. Everything else — provenance (`source`,
 * `controller`), `duration`, `guard` — is shared by both.
 *
 * ## Guards are not durations
 *
 * "While you have another Princess character in play" is a `guard`, re-evaluated on every
 * read. It flips the modifier off *and back on* as the board changes, which a duration cannot
 * do. Durations are boundaries the turn structure crosses (`expireModifiers`), and only they
 * remove the record.
 *
 * ## Dependent effects and the recursion guard (CR §6.4.4)
 *
 * A static's filter reads **modified** characteristics — "your characters with 2 {S} or less
 * get +1 {W}" must see another static's +1 {S}, and set 13's "+{S} equal to their {W}" must
 * see a modified {W}. That is a dependency graph, and a dependency graph can contain a cycle
 * (a static whose filter reads the very characteristic it modifies). The CR does not define a
 * fixpoint, so cycles are broken the only way that stays deterministic: while a card's
 * characteristic is already being computed, a re-entrant read of *that same* characteristic
 * resolves to its printed base. Non-cyclic dependencies are unaffected and evaluate in
 * dependency order, which is what CR §6.4.4 asks for.
 */
import type {
  Ability,
  Amount,
  Condition,
  Duration,
  Effect,
  KeywordName,
  Permission,
  PlayerPermission,
  Restriction,
  Selector,
  Stat,
  StaticAbility,
  StaticEffect,
} from './abilities/ir.ts';
import { cloneIr } from './abilities/ir.ts';
import type { KeywordInstance } from './abilities/keywords.ts';
import { staticAbilitiesOf } from './abilities/keywords.ts';
import type { AbilityContext } from './abilities/selectors.ts';
import {
  baseContext,
  evalAmount,
  evalCondition,
  matchesSelector,
  resolveCardRefAll,
  selectorPool,
} from './abilities/selectors.ts';
import type { CardCatalog } from './cards.ts';
import type { InstanceId, PlayerIndex, Zone } from './ids.ts';
import type { CardInstance, GameState } from './state.ts';
import { instanceAt, playerAt } from './state.ts';

// ---------------------------------------------------------------------------
// The record
// ---------------------------------------------------------------------------

/**
 * Which cards a modifier applies to — the snapshot/dynamic distinction of CR §6.4.2, as data.
 */
export type ModifierScope =
  /** A snapshot: exactly these cards, fixed when the effect resolved (CR §6.4.2.2). */
  | { readonly kind: 'cards'; readonly cards: readonly InstanceId[] }
  /** Dynamic: whatever satisfies this selector at the moment of the read (CR §6.4.2.1). */
  | { readonly kind: 'filter'; readonly filter: Selector }
  /**
   * No card at all: the effect belongs to a **player** ("this turn, you may ink an additional
   * card"). It covers no card, so every card-keyed read skips it; it names no card either, so
   * nothing about it changes when a card — its own source included — leaves play.
   */
  | { readonly kind: 'player'; readonly player: PlayerIndex };

/** What every modifier record carries, whatever it modifies. */
interface ModifierProvenance {
  /** The card the effect came from. It may have left play — an Action's effect outlives it. */
  readonly source: InstanceId;
  /** Whose effect it is: the "you" of its guard and scope, and the "your" of its duration. */
  readonly controller: PlayerIndex;
  readonly scope: ModifierScope;
  readonly duration: Duration;
  /** `state.turn` when the record was created — what a turn-boundary duration measures from. */
  readonly created: number;
  /** "while …" (CR §6.1.13): re-evaluated on every read, never stored as a result. */
  readonly guard?: Condition;
}

/**
 * A continuous-effect record (SPEC.md §4.5, CONTEXT.md "Modifier record").
 *
 * Plain, identity-free data — instance ids and IR only — so it clones, serializes and rides
 * into `playerView` unredacted, which is what lets the UI and the AI recompute {S}/{W}/lore
 * for themselves.
 *
 * `amount` is normally a plain number: a non-static "for each" is counted **once**, as the
 * effect resolves (CR §6.1.8). An `Amount` node is the deliberate exception — a *dynamic*
 * modifier, re-counted on every read.
 */
export type Modifier =
  | (ModifierProvenance & {
      readonly kind: 'stat';
      readonly stat: Stat;
      readonly amount: Amount;
    })
  | (ModifierProvenance & {
      readonly kind: 'keyword';
      readonly keyword: KeywordName;
      readonly value?: number;
    })
  /**
   * "…and loses Reckless this turn" (CR §6.6.5). Suppression, not subtraction: while the
   * record lives the card does not have the keyword at all, whatever printed it.
   */
  | (ModifierProvenance & {
      readonly kind: 'keyword-loss';
      readonly keyword: KeywordName;
    })
  /**
   * "They can't quest for the rest of this turn" — the applied half of the restriction model
   * (CR §6.6.5). Same provenance, same scope snapshot, same durations and guards as a stat
   * record; it is read by `isRestricted` instead of by a characteristic accessor.
   */
  | (ModifierProvenance & {
      readonly kind: 'restriction';
      readonly restriction: Restriction;
      /**
       * For `be-challenged`: which challengers are barred ("Damaged characters can't …"). For
       * `sing`: which songs are exempt ("…can't sing songs without Sing Together", ticket 139).
       */
      readonly by?: Selector;
    })
  /** A temporary permission that lifts one ordinary legality limiter (CR §6.6.5). */
  | (ModifierProvenance & {
      readonly kind: 'permission';
      readonly permission: Permission;
    })
  /**
   * "This turn, you may ink an additional card" — a permission given to a **player**, whose
   * `scope` is `{ kind: 'player' }` rather than a snapshot of cards (CR §6.6.5). Read by
   * `playerPermissionCount`, not by any card-keyed accessor, and `amount` is how many times
   * over the permission was given: `inkLimitOf` adds it.
   */
  | (ModifierProvenance & {
      readonly kind: 'player-permission';
      readonly permission: PlayerPermission;
      readonly amount: number;
    })
  /**
   * "Each opponent can't ready more than N of their characters at the start of their next
   * turn" (Marshmallow - Cranky Climber, ICY BLAST) — a numeric cap on the Ready step given to
   * a **player** rather than to any card, the restriction-side mirror of `player-permission`.
   * `scope` is `{ kind: 'player' }` for the same reason: the cap belongs to the player, not to
   * a snapshot of characters, so `readyStep` reads it fresh against whichever characters are
   * actually eligible when the step runs, not the ones that existed when the effect resolved.
   *
   * Multiple live records take the **most restrictive** (smallest) `amount` — CR §6.6.5's
   * "can't" always wins, so a second "can't ready more than 1" does not raise the cap to 2.
   */
  | (ModifierProvenance & {
      readonly kind: 'ready-cap';
      readonly amount: number;
    })
  /**
   * "Chosen character gains '[ability]' this turn" (CR §6.4.3). The ability is IR, so it is
   * plain data like the rest of the record; a *triggered* one is registered on the card the
   * moment the record lands and re-registered away when it expires.
   */
  | (ModifierProvenance & {
      readonly kind: 'ability';
      readonly ability: Ability;
      /**
       * Instances a `consumesOnApply` replacement (`ability.kind === 'replacement'`) has
       * already fired for — "the next time … instead", crossed off the moment it actually
       * applies rather than riding out this record's whole `duration`. Mutated in place by
       * `replacements.ts`; unused (and always empty) for every other granted ability.
       */
      readonly spentFor: InstanceId[];
    })
  /**
   * "…gains the Detective classification until the start of your next turn" (CR §5.3.3.2) — a
   * classification given, read wherever printed classifications are (`matchesSelector`).
   */
  | (ModifierProvenance & {
      readonly kind: 'subtype';
      readonly subtype: string;
    });

/** Deep-copy one record; every nested field is plain IR data. */
export function cloneModifier(modifier: Modifier): Modifier {
  return cloneIr(modifier);
}

// ---------------------------------------------------------------------------
// Creating records — the applied-static half
// ---------------------------------------------------------------------------

/**
 * Keywords the engine turns into a **triggered** ability (`abilitiesOf`). Granting or losing
 * one changes which triggers a card has, and triggers are registered rather than read on
 * demand — so these are the keywords whose grant has to re-register the affected card.
 */
const TRIGGER_KEYWORDS: readonly KeywordName[] = ['Support', 'Vanish'];

/**
 * How a modifier record tells the engine to re-register a card's triggers. Set by
 * `interpret.ts`, which owns `registerAbilitiesFor`; `modifiers.ts` has no business importing
 * it (the dependency would be circular).
 */
export type TriggerRefresh = (state: GameState, cards: readonly InstanceId[]) => void;

let refreshTriggers: TriggerRefresh = () => {};

/** Wired once, at module load, by `abilities/interpret.ts`. */
export function setTriggerRefresh(refresh: TriggerRefresh): void {
  refreshTriggers = refresh;
}

/** Whether a record's arrival or departure changes which triggered abilities a card has. */
function changesTriggers(modifier: Modifier): boolean {
  // A granted ability may itself be a triggered one, and triggers are registered rather than
  // read on demand — so a grant either way has to re-register the card it lands on.
  if (modifier.kind === 'ability') return true;
  if (modifier.kind !== 'keyword' && modifier.kind !== 'keyword-loss') return false;
  return TRIGGER_KEYWORDS.includes(modifier.keyword);
}

/** The cards a record's snapshot names; a dynamic scope names none in particular. */
function snapshotOf(modifier: Modifier): readonly InstanceId[] {
  return modifier.scope.kind === 'cards' ? modifier.scope.cards : [];
}

/** Record a continuous effect. The only way a `Modifier` ever enters the state. */
export function addModifier(state: GameState, modifier: Modifier): void {
  state.modifiers.push(modifier);
  // Support and Vanish are *triggered* abilities the engine derives from a keyword, and
  // triggers are registered as a card enters play — so a card that gains one while already in
  // play has to be re-registered, or the keyword would be visible and inert.
  if (changesTriggers(modifier)) refreshTriggers(state, snapshotOf(modifier));
}

/** The effect nodes that leave a modifier record behind, narrowed. */
export type ModifierEffect = Extract<
  Effect,
  {
    kind:
      | 'modify'
      | 'grant-keyword'
      | 'lose-keyword'
      | 'restrict'
      | 'allow'
      | 'grant-ability'
      | 'grant-subtype';
  }
>;

/**
 * What one applied static did, for the caller to narrate (`events.ts`'s `modifier-applied`).
 *
 * The record itself is not handed back: it is state, and a caller holding one would be a
 * caller able to edit it. This is the read-only summary of it, which is all narration needs.
 */
export interface AppliedModifier {
  readonly instances: readonly InstanceId[];
  readonly change: 'stat' | 'keyword' | 'keyword-loss' | 'restriction' | 'permission' | 'ability' | 'subtype';
  readonly what: string;
  readonly amount: number | null;
}

/**
 * Resolve one `modify` / `grant-keyword` effect into a record — the moment an **applied**
 * static snapshots (CR §6.4.2.2).
 *
 * Both halves of the snapshot happen here: the cards (`scope`'s pool, or the effect's
 * resolved references, evaluated now) and the amount (counted once, unless the node asks to
 * stay dynamic). Nothing is left to re-resolve later, which is exactly what makes a character
 * that arrives afterwards unaffected.
 *
 * Returns what it applied, or `null` when the effect found nobody — the caller turns that into
 * the event that says a continuous effect landed, because a modifier moves no card and would
 * otherwise be the one kind of effect the event stream does not mention.
 */
export function applyModifierEffect(
  state: GameState,
  ctx: AbilityContext,
  node: ModifierEffect,
): AppliedModifier | null {
  const cards = snapshotTargets(state, ctx, node);
  if (cards.length === 0) return null; // "do as much as you can" (CR §1.2.3)
  const provenance: ModifierProvenance = {
    source: ctx.source,
    controller: ctx.controller,
    scope: { kind: 'cards', cards },
    duration: node.duration,
    created: state.turn,
    ...(node.guard === undefined ? {} : { guard: node.guard }),
  };
  switch (node.kind) {
    case 'modify': {
      // CR §6.1.8: a non-static "for each" is counted once, here — unless the card says
      // otherwise, in which case the `Amount` rides into the record and is re-counted on read.
      const dynamic = node.dynamic === true;
      const amount = dynamic ? cloneIr(node.amount) : evalAmount(state, ctx, node.amount);
      addModifier(state, { ...provenance, kind: 'stat', stat: node.stat, amount });
      return {
        instances: cards,
        change: 'stat',
        what: node.stat,
        amount: dynamic ? null : (amount as number),
      };
    }
    case 'grant-keyword':
      addModifier(state, {
        ...provenance,
        kind: 'keyword',
        keyword: node.keyword,
        ...(node.value === undefined ? {} : { value: node.value }),
      });
      return {
        instances: cards,
        change: 'keyword',
        what: node.keyword,
        amount: node.value ?? null,
      };
    case 'lose-keyword':
      addModifier(state, { ...provenance, kind: 'keyword-loss', keyword: node.keyword });
      return { instances: cards, change: 'keyword-loss', what: node.keyword, amount: null };
    case 'restrict':
      addModifier(state, {
        ...provenance,
        kind: 'restriction',
        restriction: node.restriction,
        ...(node.by === undefined ? {} : { by: cloneIr(node.by) }),
      });
      return { instances: cards, change: 'restriction', what: node.restriction, amount: null };
    case 'allow':
      addModifier(state, { ...provenance, kind: 'permission', permission: node.permission });
      return { instances: cards, change: 'permission', what: node.permission, amount: null };
    case 'grant-ability':
      addModifier(state, {
        ...provenance,
        kind: 'ability',
        ability: cloneIr(node.ability),
        spentFor: [],
      });
      return {
        instances: cards,
        change: 'ability',
        // The granted ability's own printed name, where the card that grants it names one
        // ("gains 'CEASELESS WORKER'"); a keyword granted this way names itself.
        what:
          node.ability.kind === 'keyword' ? node.ability.keyword : (node.ability.name ?? ''),
        amount: node.ability.kind === 'keyword' ? (node.ability.value ?? null) : null,
      };
    case 'grant-subtype':
      addModifier(state, { ...provenance, kind: 'subtype', subtype: node.subtype });
      return { instances: cards, change: 'subtype', what: node.subtype, amount: null };
  }
}

/** The one effect node that leaves a **player-scoped** record behind, narrowed. */
export type PlayerPermissionEffect = Extract<Effect, { kind: 'allow-player' }>;

/**
 * Resolve one `allow-player` effect into a record — the player-scoped counterpart of
 * `applyModifierEffect`.
 *
 * There is nothing to snapshot: the record names a player, so it never has to be pruned, never
 * has to be retargeted by Shift, and never notices its source leaving play. Its duration is
 * the ordinary `Duration` vocabulary and `expireModifiers` drops it at the ordinary boundary,
 * which is what makes "this turn" mean this turn and nothing more.
 */
export function applyPlayerPermissionEffect(
  state: GameState,
  ctx: AbilityContext,
  node: PlayerPermissionEffect,
  player: PlayerIndex,
): void {
  addModifier(state, {
    kind: 'player-permission',
    permission: node.permission,
    amount: node.amount ?? 1,
    source: ctx.source,
    controller: ctx.controller,
    scope: { kind: 'player', player },
    duration: node.duration,
    created: state.turn,
    ...(node.guard === undefined ? {} : { guard: cloneIr(node.guard) }),
  });
}

/** The one effect node that leaves a **player-scoped ready cap** record behind, narrowed. */
export type ReadyCapEffect = Extract<Effect, { kind: 'cap-ready' }>;

/**
 * Resolve one `cap-ready` effect into a record — the restriction-side counterpart of
 * `applyPlayerPermissionEffect`. Same story: nothing to snapshot, since the record names a
 * player rather than a card, so it never has to be pruned or retargeted and does not care
 * whether its source is still in play by the time the capped player's Ready step runs.
 */
export function applyReadyCapEffect(
  state: GameState,
  ctx: AbilityContext,
  node: ReadyCapEffect,
  player: PlayerIndex,
): void {
  addModifier(state, {
    kind: 'ready-cap',
    amount: node.amount,
    source: ctx.source,
    controller: ctx.controller,
    scope: { kind: 'player', player },
    duration: node.duration,
    created: state.turn,
    ...(node.guard === undefined ? {} : { guard: cloneIr(node.guard) }),
  });
}

/** The cards an applied static lands on: its `scope`'s pool, or its resolved references. */
function snapshotTargets(
  state: GameState,
  ctx: AbilityContext,
  node: ModifierEffect,
): InstanceId[] {
  const named =
    node.scope !== undefined
      ? selectorPool(state, ctx, node.scope)
      : node.target === undefined
        ? []
        : resolveCardRefAll(ctx, node.target);
  return named.filter((id) => instanceAt(state, id).zone === 'play');
}

// ---------------------------------------------------------------------------
// Reading — nothing is cached (SPEC.md §4.5)
// ---------------------------------------------------------------------------

/** Normalise a printed ability name for suppression matching (ticket 118). */
export function normaliseAbilityName(name: string): string {
  return name.trim().toUpperCase();
}

/**
 * Normalized names of abilities a continuous `suppress-ability` static has taken **away** from
 * this card (CR §6.6.5's "loses" pattern, applied to a whole named ability rather than a
 * keyword) — "Your Gargoyle characters lose the Stone by Day ability." Suppression is
 * absolute, exactly like `lostKeywords`: it removes the named ability however it reached the
 * card, printed or granted, matched by normalized name rather than display text.
 *
 * This is the one read path `abilitiesOf` (`interpret.ts`), `replacementsOf`
 * (`replacements.ts`) and `activeStaticAbilitiesOf` (below) all consult, so a suppressed
 * ability disappears from triggers, replacements and continuous statics alike.
 */
export function suppressedAbilityNames(state: GameState, card: CardInstance): readonly string[] {
  const key = guardKey(card.id, SUPPRESSED_SLOT);
  if (evaluating.has(key)) return NO_SUPPRESSED;
  evaluating.add(key);
  try {
    let names: string[] | null = null;
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        // Raw, unsuppressed reads: whether a suppressor's *own* ability is itself suppressed is
        // not a question any printed card asks, and reading it through `activeStaticAbilitiesOf`
        // here would recurse into this very function.
        for (const ability of staticAbilitiesOf(state.catalog, source.defId)) {
          const effect = ability.effect;
          if (effect.kind !== 'suppress-ability') continue;
          if (!staticApplies(state, source, ability, card)) continue;
          const normalized = normaliseAbilityName(effect.name);
          if (names === null) names = [normalized];
          else if (!names.includes(normalized)) names.push(normalized);
        }
      }
    }
    return names ?? NO_SUPPRESSED;
  } finally {
    evaluating.delete(key);
  }
}

/** Whether a card's own ability of this (possibly unset) printed name is currently suppressed. */
export function abilityNameIsSuppressed(
  state: GameState,
  card: CardInstance,
  name: string | undefined,
): boolean {
  if (name === undefined) return false;
  const suppressed = suppressedAbilityNames(state, card);
  return suppressed.length > 0 && suppressed.includes(normaliseAbilityName(name));
}

/**
 * A card's continuous static abilities with any named suppression (CR §6.6.5) already applied
 * — the one chokepoint every static-driven read in this file consults, so "loses [ability]"
 * holds for `statBonus`, `grantedKeywords`, `isRestricted` and every sibling below exactly as
 * it holds for `abilitiesOf`'s triggered/replacement reads.
 */
export function activeStaticAbilitiesOf(
  state: GameState,
  source: CardInstance,
): readonly StaticAbility[] {
  const all = staticAbilitiesOf(state.catalog, source.defId);
  if (all.length === 0) return all;
  const suppressed = suppressedAbilityNames(state, source);
  if (suppressed.length === 0) return all;
  return all.filter(
    (ability) => ability.name === undefined || !suppressed.includes(normaliseAbilityName(ability.name)),
  );
}

/**
 * Every live contribution to one characteristic of one card, combined simultaneously
 * (CR §6.6): the applied statics whose snapshot names it, plus the continuous statics of
 * every card in play whose filter it matches right now.
 *
 * The sum is deliberately **unclamped** — a negative {S} stays negative for further
 * modification (CR §6.6.2), and the clamping belongs at the reader (`characteristics.ts`).
 */
export function statBonus(state: GameState, card: CardInstance, stat: Stat): number {
  const key = guardKey(card.id, STAT_SLOT[stat]);
  if (evaluating.has(key)) return 0;
  evaluating.add(key);
  try {
    let total = 0;
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'stat' || modifier.stat !== stat) continue;
      if (!modifierApplies(state, modifier, card)) continue;
      total += amountOf(state, modifier.amount, modifier.source, modifier.controller, card);
    }
    // The play zones are the maintained index of in-play cards, and a sum is order-blind —
    // so this walks ~a dozen ids rather than every instance in the game.
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of activeStaticAbilitiesOf(state, source)) {
          const effect = ability.effect;
          if (effect.kind !== 'buff' || effect.stat !== stat) continue;
          if (!staticApplies(state, source, ability, card)) continue;
          total += amountOf(state, effect.amount, source.id, source.controller, card);
        }
      }
    }
    return total;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Every keyword granted to one card by a continuous effect — another card's static ability or
 * a resolved `grant-keyword`. Computed on read like everything else, so a card that arrives
 * under an active static has the keyword the moment it lands.
 */
export function grantedKeywords(state: GameState, card: CardInstance): KeywordInstance[] {
  const key = guardKey(card.id, KEYWORDS_SLOT);
  if (evaluating.has(key)) return NO_KEYWORDS;
  evaluating.add(key);
  try {
    let granted: KeywordInstance[] | null = null;
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'keyword') continue;
      if (!modifierApplies(state, modifier, card)) continue;
      (granted ??= []).push({ keyword: modifier.keyword, value: modifier.value ?? null });
    }
    // Play-zone order, not instance order: a keyword list is a membership set to every
    // consumer (`hasKeyword`, `keywordValue`'s sum and max), so source order cannot show.
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of activeStaticAbilitiesOf(state, source)) {
          const effect = ability.effect;
          if (effect.kind !== 'grant-keyword') continue;
          if (!staticApplies(state, source, ability, card)) continue;
          (granted ??= []).push({
            keyword: effect.keyword,
            // Counted continuously, like a `buff`'s amount (CR §6.1.8) — "Resist +1 for each
            // location you have in play" re-reads the count every time this is read.
            value:
              effect.value === undefined
                ? null
                : amountOf(state, effect.value, source.id, source.controller, card),
          });
        }
      }
    }
    return granted ?? NO_KEYWORDS;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Keywords a continuous effect has taken **away** from this card (CR §6.6.5) — "and loses
 * Reckless this turn", or a continuous "Characters named Peter Pan lose Evasive". Suppression is
 * absolute: `keywordsOf` drops every instance of a lost keyword, printed, authored or granted,
 * which is what "loses" means — and, because that drop happens *after* `keywordsOf` folds every
 * grant in (base and continuous alike), a later `grant-keyword` never restores a keyword this
 * function still lists. That is the whole of the "can't gain" denial half — no separate record
 * or read path for it, the same way `suppressedAbilityNames` needs none for `suppress-ability`.
 */
export function lostKeywords(state: GameState, card: CardInstance): KeywordName[] {
  const key = guardKey(card.id, KEYWORD_LOSS_SLOT);
  if (evaluating.has(key)) return NO_KEYWORD_NAMES;
  evaluating.add(key);
  try {
    let lost: KeywordName[] | null = null;
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'keyword-loss') continue;
      if (!modifierApplies(state, modifier, card)) continue;
      if (lost === null) lost = [modifier.keyword];
      else if (!lost.includes(modifier.keyword)) lost.push(modifier.keyword);
    }
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of activeStaticAbilitiesOf(state, source)) {
          const effect = ability.effect;
          if (effect.kind !== 'lose-keyword') continue;
          if (!staticApplies(state, source, ability, card)) continue;
          if (lost === null) lost = [effect.keyword];
          else if (!lost.includes(effect.keyword)) lost.push(effect.keyword);
        }
      }
    }
    return lost ?? NO_KEYWORD_NAMES;
  } finally {
    evaluating.delete(key);
  }
}

/** The `Modifier` shape a resolved `grant-ability` effect leaves behind. */
export type AbilityModifier = Extract<Modifier, { kind: 'ability' }>;

/** One ability a continuous effect has granted a card, paired with where it came from. */
export interface GrantedAbilityEntry {
  readonly ability: Ability;
  /**
   * The modifier record this ability rides on, when there is one — `null` for a continuous
   * static's grant, which re-derives the ability fresh on every read rather than snapshotting
   * a record. `replacements.ts` reads this to cross a `consumesOnApply` replacement off: with
   * no record, there is nothing to mark, so a static-granted one just keeps re-applying.
   */
  readonly modifier: AbilityModifier | null;
}

const NO_GRANTED_ENTRIES: GrantedAbilityEntry[] = [];

/** Whether a granted `consumesOnApply` replacement has already fired for this specific card. */
function isSpentReplacement(modifier: AbilityModifier, cardId: InstanceId): boolean {
  return (
    modifier.ability.kind === 'replacement' &&
    modifier.ability.consumesOnApply === true &&
    modifier.spentFor.includes(cardId)
  );
}

/**
 * Every ability a continuous effect has given this card (CR §6.4.3) — a resolved
 * `grant-ability`, or another card's `grant-ability` static — paired with the modifier record
 * behind it, if any. The one computation both `grantedAbilities` (below) and `replacements.ts`
 * read, so the two can never disagree about which entry is which.
 */
function grantedAbilityEntries(state: GameState, card: CardInstance): GrantedAbilityEntry[] {
  const key = guardKey(card.id, ABILITIES_SLOT);
  if (evaluating.has(key)) return NO_GRANTED_ENTRIES;
  evaluating.add(key);
  try {
    let granted: GrantedAbilityEntry[] | null = null;
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'ability') continue;
      if (!modifierApplies(state, modifier, card)) continue;
      if (isSpentReplacement(modifier, card.id)) continue;
      (granted ??= []).push({ ability: modifier.ability, modifier });
    }
    // Ascending instance id — **not** raw play-zone order: a granted ability's position in
    // `abilitiesOf` is what an `activate:i7:a2` key names, so source order is observable here.
    for (const id of inPlayAscending(state)) {
      const source = instanceAt(state, id);
      for (const ability of activeStaticAbilitiesOf(state, source)) {
        const effect = ability.effect;
        if (effect.kind !== 'grant-ability') continue;
        if (!staticApplies(state, source, ability, card)) continue;
        (granted ??= []).push({ ability: effect.ability, modifier: null });
      }
    }
    return granted ?? NO_GRANTED_ENTRIES;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Whole abilities this card has been given by a continuous effect (CR §6.4.3). Merged into
 * `abilitiesOf`, so a granted ability behaves exactly as a printed one.
 */
export function grantedAbilities(state: GameState, card: CardInstance): Ability[] {
  const entries = grantedAbilityEntries(state, card);
  return entries.length === 0 ? NO_ABILITIES : entries.map((entry) => entry.ability);
}

/**
 * The same reads as `grantedAbilities`, but keeping each entry's originating modifier record —
 * what `replacements.ts`'s `replacementsOf` needs to cross a `consumesOnApply` replacement off
 * the instant it applies, rather than only once its whole granted `duration` runs out.
 */
export function grantedAbilityEntriesOf(
  state: GameState,
  card: CardInstance,
): readonly GrantedAbilityEntry[] {
  return grantedAbilityEntries(state, card);
}

/**
 * Classifications a continuous effect has given this card — either a resolved `grant-subtype`
 * effect's applied, fixed-duration modifier (CR §5.3.3.2: "gains the Detective classification
 * until the start of your next turn"), or another card's `grant-subtype` **static** ability,
 * computed on read like `grantedKeywords` so a character that arrives later under an active
 * static has the classification the moment it lands, and loses it the instant the granting
 * source leaves play. Read alongside the printed ones wherever a selector asks about a
 * classification, so a granted one is indistinguishable from a printed one for as long as it
 * holds.
 */
export function grantedSubtypes(state: GameState, card: CardInstance): string[] {
  const key = guardKey(card.id, SUBTYPES_SLOT);
  if (evaluating.has(key)) return NO_SUBTYPES;
  evaluating.add(key);
  try {
    let granted: string[] | null = null;
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'subtype') continue;
      if (!modifierApplies(state, modifier, card)) continue;
      if (granted === null) granted = [modifier.subtype];
      else if (!granted.includes(modifier.subtype)) granted.push(modifier.subtype);
    }
    // Play-zone order, not instance order: like `grantedKeywords`, this is a membership set
    // to every consumer, so source order cannot show.
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of activeStaticAbilitiesOf(state, source)) {
          const effect = ability.effect;
          if (effect.kind !== 'grant-subtype') continue;
          if (!staticApplies(state, source, ability, card)) continue;
          if (granted === null) granted = [effect.subtype];
          else if (!granted.includes(effect.subtype)) granted.push(effect.subtype);
        }
      }
    }
    return granted ?? NO_SUBTYPES;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Whether a continuous effect stops one characteristic of this card from being reduced below
 * its own printed value (Elisa Maza - Transformed Gargoyle's FOREVER STRONG) — the read path
 * for `StaticEffect.stat-floor`, consulted by `characteristics.ts`'s stat readers *after* every
 * ordinary modifier has already been summed (CR §6.6: no layers, so the floor is just one more
 * thing evaluated on every read). It only ever stops a reduction: a caller only asks this once
 * the raw total has already fallen below the printed value, so an ordinary bonus that keeps the
 * total at or above it never has to consult this at all.
 *
 * Continuous-static only, the same shape as `restrict-lore-gain` and `raise-lore-to-win`: no
 * printed card resolves this as an applied effect, so there is no `Modifier` half to read.
 */
export function hasPrintedFloor(state: GameState, card: CardInstance, stat: Stat): boolean {
  const key = guardKey(card.id, FLOOR_SLOT[stat]);
  if (evaluating.has(key)) return false;
  evaluating.add(key);
  try {
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of staticAbilitiesOf(state.catalog, source.defId)) {
          const effect = ability.effect;
          if (effect.kind !== 'stat-floor' || effect.stat !== stat) continue;
          if (!staticApplies(state, source, ability, card)) continue;
          return true;
        }
      }
    }
    return false;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Whether a continuous effect forbids this card from doing something (CR §6.6.5) — the read
 * path for `restrict`, consulted by the legality predicates rather than by a characteristic.
 *
 * The third read path beside `statBonus` and `grantedKeywords`, and deliberately the same
 * shape: applied records whose snapshot names the card, plus the continuous `restrict` statics
 * of every card in play whose filter it matches **right now**. A "can't" always wins, so every
 * legality predicate asks this and stops.
 *
 * The card need not be in play: "opponents can't play actions" is a restriction on a card in a
 * hand, which is exactly why the static half matches through the live selector rather than
 * through a snapshot.
 *
 * Nor need the **source**: "you can't play this character unless …" is a restriction a card
 * prints about itself, and it has to function while the card is still in hand or it could
 * never say anything (CR §6.1.12's only-makes-sense exception). So a card outside play also
 * reads its *own* printed `restrict` statics — its own abilities about itself, nothing wider.
 *
 * A **`play`** restriction is the one read whose filter is not pinned to a zone: a card is
 * played from wherever it is (CR §4.3.1) — a hand, a discard, the top of a deck — so the
 * filter is read in the zone the play is actually happening from. `costs.ts`'s
 * `matchesCostFilter` skips the zone for the same reason: the card is the subject of the play,
 * not a card sitting somewhere. Every other restriction keeps its filter's own zone.
 *
 * `against` is consulted by `be-challenged` (whose `by` selector says which challengers are
 * barred — "Damaged characters can't challenge this character") and by `sing` (whose `by`
 * selector says which *songs* are exempt — "this character can't sing songs without Sing
 * Together", ticket 139: `by: { withoutKeyword: 'Sing Together' }` bars every song but those
 * that carry it). Every other restriction ignores it.
 */
export function isRestricted(
  state: GameState,
  card: CardInstance,
  restriction: Restriction,
  against?: CardInstance,
): boolean {
  const key = guardKey(card.id, RESTRICT_SLOT[restriction], (against?.id ?? -1) + 2);
  if (evaluating.has(key)) return false;
  evaluating.add(key);
  const asZone = restriction === 'play' ? card.zone : undefined;
  try {
    for (const modifier of state.modifiers) {
      if (modifier.kind !== 'restriction' || modifier.restriction !== restriction) continue;
      if (!modifierApplies(state, modifier, card, asZone)) continue;
      if (barred(state, modifier.source, modifier.controller, modifier.by, against)) return true;
    }
    for (const player of state.players) {
      for (const id of player.play) {
        const source = instanceAt(state, id);
        for (const ability of activeStaticAbilitiesOf(state, source)) {
          const effect = ability.effect;
          if (effect.kind !== 'restrict' || effect.restriction !== restriction) continue;
          if (!staticApplies(state, source, ability, card, asZone)) continue;
          if (barred(state, source.id, source.controller, effect.by, against)) return true;
        }
      }
    }
    // The self-referential read described above: a card not in play consults its own printed
    // restrict statics (in play, the loop above already read them). Source and subject are the
    // same card, so only a filter that names the card where it actually is can match.
    if (card.zone !== 'play') {
      for (const ability of activeStaticAbilitiesOf(state, card)) {
        const effect = ability.effect;
        if (effect.kind !== 'restrict' || effect.restriction !== restriction) continue;
        if (!staticApplies(state, card, ability, card, asZone)) continue;
        if (barred(state, card.id, card.controller, effect.by, against)) return true;
      }
    }
    return false;
  } finally {
    evaluating.delete(key);
  }
}

/**
 * Whether a restriction with an optional `by` selector catches this second card — the
 * challenger for `be-challenged` (always in play), the song for `sing` (always in hand while
 * this is asked). `Selector`'s own default zone is `'play'` (`matchesSelector`), which a
 * `be-challenged` `by` selector wants unstated but a `sing` one over a song in hand does not —
 * so, exactly like `isRestricted`'s own `asZone` scopes the *filter* to a restricted card
 * outside play, this scopes the *`by`* selector to `against`'s own zone unless the card module
 * already said which zone it meant.
 */
function barred(
  state: GameState,
  source: InstanceId,
  controller: PlayerIndex,
  by: Selector | undefined,
  against: CardInstance | undefined,
): boolean {
  if (by === undefined) return true;
  if (against === undefined) return false;
  const scoped = by.zone === undefined ? { ...by, zone: against.zone } : by;
  return matchesSelector(state, contextFor(source, controller, against), against, scoped);
}

/**
 * Whether a continuous effect has given this card a permission it does not print.
 *
 * `target` is the card the permission would be exercised against — for `challenge-ready`, the
 * character being considered as a challenge target. A grant with a `condition` selector (see
 * `StaticEffect.allow`) only counts if `target` is supplied and matches it; a permission with
 * no target condition, such as `quest-while-drying`, is indifferent to `target`.
 */
export function isAllowed(
  state: GameState,
  card: CardInstance,
  permission: Permission,
  target?: CardInstance,
): boolean {
  for (const modifier of state.modifiers) {
    if (modifier.kind !== 'permission' || modifier.permission !== permission) continue;
    if (!modifierApplies(state, modifier, card)) continue;
    return true;
  }
  for (const player of state.players) {
    for (const id of player.play) {
      const source = instanceAt(state, id);
      for (const ability of activeStaticAbilitiesOf(state, source)) {
        const effect = ability.effect;
        if (effect.kind !== 'allow' || effect.permission !== permission) continue;
        if (!staticApplies(state, source, ability, card)) continue;
        if (!permissionCovers(state, source, effect.condition, target)) continue;
        return true;
      }
    }
  }
  return false;
}

/**
 * Whether a permission grant's optional `condition` selector covers this target — the reverse
 * of `barred`: absent, the grant is unconditional; present, it only covers a target supplied
 * and matching it (no target supplied means the caller isn't asking about a specific one, so a
 * conditional grant can't be confirmed and does not count).
 */
function permissionCovers(
  state: GameState,
  source: CardInstance,
  condition: Selector | undefined,
  target: CardInstance | undefined,
): boolean {
  if (condition === undefined) return true;
  if (target === undefined) return false;
  return matchesSelector(state, contextFor(source.id, source.controller, target), target, condition);
}

/**
 * Whether a continuous effect compels **opponents** of this card's controller to choose it
 * for actions and abilities if able (ticket 119) — the read path for `StaticEffect.must-be-
 * chosen`, consulted by `targetPool` (`abilities/selectors.ts`).
 *
 * `chooser` is whoever is actually picking the target (`targetPool`'s `chooser`, not
 * necessarily the ability's controller — see `chooserOf`). A card never compels its own
 * controller: only an opposing chooser is bound, which is what the printed "opponents must
 * choose" scopes to. There is no applied-modifier half, unlike `isRestricted`/`isAllowed`:
 * every printed instance of this static is a card's own continuous ability, tied to being in
 * play, exactly like Bodyguard's must-choose challenge limiter.
 */
export function mustBeChosenBy(state: GameState, card: CardInstance, chooser: PlayerIndex): boolean {
  if (card.controller === chooser) return false;
  for (const player of state.players) {
    for (const id of player.play) {
      const source = instanceAt(state, id);
      for (const ability of staticAbilitiesOf(state.catalog, source.defId)) {
        const effect = ability.effect;
        if (effect.kind !== 'must-be-chosen') continue;
        if (!staticApplies(state, source, ability, card)) continue;
        return true;
      }
    }
  }
  return false;
}

/**
 * How many times over a player currently holds a player-scoped permission (CR §6.6.5) — the
 * read path for the `player-permission` record, the way `isAllowed` is the read path for the
 * card-scoped one.
 *
 * A count rather than a boolean because the one permission that exists is a *quantity*: two
 * copies of "you may ink an additional card" let you ink two more. Same discipline as every
 * other continuous read — computed on read, the record's `guard` re-evaluated each time,
 * nothing cached. There is no continuous-static half here: a static that raises the ink limit
 * is already `StaticEffect.extra-ink`, read from `player.play` by `inkLimitOf` itself.
 */
export function playerPermissionCount(
  state: GameState,
  player: PlayerIndex,
  permission: PlayerPermission,
): number {
  let total = 0;
  for (const modifier of state.modifiers) {
    if (modifier.kind !== 'player-permission' || modifier.permission !== permission) continue;
    if (modifier.scope.kind !== 'player' || modifier.scope.player !== player) continue;
    if (
      modifier.guard !== undefined &&
      // The record belongs to a player, so there is no subject card for its guard to read —
      // "while you have a character in play" and its kin are player-scoped conditions anyway.
      !evalCondition(state, baseContext(modifier.source, modifier.controller, null), modifier.guard)
    ) {
      continue;
    }
    total += modifier.amount;
  }
  return total;
}

/**
 * How many of this player's characters may ready this Ready step — the read path for the
 * `ready-cap` record, the way `playerPermissionCount` is the read path for `player-permission`.
 *
 * `null` means uncapped (the ordinary CR §3.2.1 Ready step: everything eligible readies). A
 * live cap is the **most restrictive** of every applicable record — CR §6.6.5's "can't" always
 * wins, so two copies of "can't ready more than 1" still cap at 1, not 2. Same discipline as
 * every other continuous read: computed on read, each record's `guard` re-evaluated every time,
 * nothing cached.
 */
export function readyCapOf(state: GameState, player: PlayerIndex): number | null {
  let cap: number | null = null;
  for (const modifier of state.modifiers) {
    if (modifier.kind !== 'ready-cap') continue;
    if (modifier.scope.kind !== 'player' || modifier.scope.player !== player) continue;
    if (
      modifier.guard !== undefined &&
      // Player-scoped, like `player-permission`: no subject card for the guard to read.
      !evalCondition(state, baseContext(modifier.source, modifier.controller, null), modifier.guard)
    ) {
      continue;
    }
    if (cap === null || modifier.amount < cap) cap = modifier.amount;
  }
  return cap;
}

/**
 * One player's per-turn ink limit (CR §4.2): 1, plus one addition for each live "ink an
 * additional card" static they control, plus any the player has been *given* for a duration
 * ("This turn, you may ink an additional card" — a `player-permission` record, which needs no
 * card in play and so survives the Action that granted it going to the discard).
 *
 * Player-scoped, so it is its own read path rather than a `staticApplies` filter match — but
 * the same discipline as every other continuous static: computed on read, `while` guards
 * re-evaluated each time, nothing cached. A source that leaves play mid-turn simply stops
 * counting, which only constrains the *next* ink action — cards already inked stay inked.
 */
export function inkLimitOf(state: GameState, player: PlayerIndex): number {
  let limit = 1 + playerPermissionCount(state, player, 'extra-ink');
  for (const id of playerAt(state, player).play) {
    const source = instanceAt(state, id);
    for (const ability of activeStaticAbilitiesOf(state, source)) {
      const effect = ability.effect;
      if (effect.kind !== 'extra-ink') continue;
      if (
        ability.while !== undefined &&
        !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
      ) {
        continue;
      }
      limit += effect.amount;
    }
  }
  return limit;
}

/**
 * Whether every card in this player's hand counts as having the inkwell symbol (CR §4.2) —
 * true while they control a live `hand-inkable` static ("All cards in your hand count as
 * having ◉"). Player-scoped like `inkLimitOf`, and the same discipline: computed on read,
 * `while` guards re-evaluated each time, nothing cached. It answers only the symbol check —
 * the per-turn limit is `inkLimitOf`'s question, and both are asked at enumeration *and* at
 * apply time (`rules.ts`), so a source that leaves play mid-turn stops answering immediately.
 */
export function handCountsAsInkable(state: GameState, player: PlayerIndex): boolean {
  for (const id of playerAt(state, player).play) {
    const source = instanceAt(state, id);
    for (const ability of activeStaticAbilitiesOf(state, source)) {
      if (ability.effect.kind !== 'hand-inkable') continue;
      if (
        ability.while !== undefined &&
        !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
      ) {
        continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * Whether this player's discard pile counts as a legal origin for the ordinary CR §4.2 ink
 * turn action — true while they control a live `discard-inkable` static ("You can ink cards
 * from your discard", Moana - Curious Explorer's ANCESTRAL LEGACY). Player-scoped like
 * `handCountsAsInkable`, and the same discipline: computed on read, `while` guards
 * re-evaluated each time, nothing cached — so the source leaving play immediately removes the
 * origin from both enumeration (`legalTurnActions`) and apply-time revalidation (`rules.ts`'s
 * `inkCardFromDiscard`). It answers only "is the discard a legal zone to ink from" — the
 * printed inkwell symbol is still required, and the per-turn limit is still `inkLimitOf`'s
 * question.
 */
export function discardCountsAsInkable(state: GameState, player: PlayerIndex): boolean {
  for (const id of playerAt(state, player).play) {
    const source = instanceAt(state, id);
    for (const ability of staticAbilitiesOf(state.catalog, source.defId)) {
      if (ability.effect.kind !== 'discard-inkable') continue;
      if (
        ability.while !== undefined &&
        !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
      ) {
        continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * Whether this player's Draw step (CR §3.2.3) is skipped — true while they control a live
 * `skip-draw` static ("Skip your turn's Draw step", Arthur - Determined Squire). Player-scoped
 * like `inkLimitOf`, and the same discipline: computed on read, `while` guards re-evaluated
 * each time, nothing cached — so the suppression lifts the instant the source leaves play.
 * Read by `rules.ts`'s `drawStep`, the one chokepoint the step passes through, right alongside
 * the game's own built-in first-turn skip (CR §3.2.3.1).
 */
export function drawStepSkipped(state: GameState, player: PlayerIndex): boolean {
  for (const id of playerAt(state, player).play) {
    const source = instanceAt(state, id);
    for (const ability of activeStaticAbilitiesOf(state, source)) {
      if (ability.effect.kind !== 'skip-draw') continue;
      if (
        ability.while !== undefined &&
        !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
      ) {
        continue;
      }
      return true;
    }
  }
  return false;
}

/**
 * The characteristic this player's characters use to deal challenge damage. Strength is the
 * CR default; a live `challenge-damage-stat` static replaces it for its controller. Like the
 * other player-scoped static reads, this is computed from cards currently in play on every
 * call, including the ability's ordinary `while` guard.
 */
export function challengeDamageStatOf(
  state: GameState,
  player: PlayerIndex,
): 'strength' | 'willpower' {
  for (const id of playerAt(state, player).play) {
    const source = instanceAt(state, id);
    for (const ability of activeStaticAbilitiesOf(state, source)) {
      const effect = ability.effect;
      if (effect.kind !== 'challenge-damage-stat') continue;
      if (
        ability.while !== undefined &&
        !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
      ) {
        continue;
      }
      return effect.stat;
    }
  }
  return 'strength';
}

/**
 * Whether this player may **gain lore** right now (CR §6.6.5's "can't") — false while any live
 * `restrict-lore-gain` static covers them and its printed exception does not hold. Player-scoped
 * like `inkLimitOf`, and the same discipline: computed on read, `while` guards re-evaluated each
 * time, nothing cached — so the restriction lifts the instant the source stops being exerted or
 * leaves play.
 *
 * The reads are deliberately from two different seats. The static's own `while` ("While this
 * character is exerted") is the ability's *functioning* condition and is read against its
 * controller, as every other static's is. The `unless` clause is read against the **restricted
 * player**, who is the "you" of its selectors — the printed text speaks about them ("unless one
 * of *their* characters has challenged this turn") — while `self` still names the source card.
 *
 * Read at the one chokepoint every gain passes through (`gainLoreEvent`, `damage.ts`), so
 * questing, a location's ◊ and an effect's "gain N lore" are stopped alike.
 */
export function canGainLore(state: GameState, player: PlayerIndex): boolean {
  for (const each of state.players) {
    for (const id of each.play) {
      const source = instanceAt(state, id);
      for (const ability of activeStaticAbilitiesOf(state, source)) {
        const effect = ability.effect;
        if (effect.kind !== 'restrict-lore-gain') continue;
        if (effect.players === 'you' && source.controller !== player) continue;
        if (effect.players === 'opponents' && source.controller === player) continue;
        if (
          ability.while !== undefined &&
          !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
        ) {
          continue;
        }
        // "…unless [the restricted player has done X]" — their seat is the "you" of the clause.
        if (
          effect.unless !== undefined &&
          evalCondition(state, contextFor(source.id, player, source), effect.unless)
        ) {
          continue;
        }
        return false;
      }
    }
  }
  return true;
}

/**
 * This player's CR §1.8.1.1 lore-to-win threshold: `state.rules.loreToWin`, plus one addition
 * for each live `raise-lore-to-win` static that covers them ("Opponents need 25 lore to win
 * the game"). Player-scoped like `inkLimitOf`, and the same discipline: computed on read,
 * `while` guards re-evaluated each time, nothing cached — each copy adds its own `amount`, so
 * two raise a covered player's threshold by twice as much. Read at the one chokepoint the CR
 * §1.8.1.1 win check passes through (`runGameStateChecks`, `rules.ts`).
 */
export function loreToWinOf(state: GameState, player: PlayerIndex): number {
  let threshold = state.rules.loreToWin;
  for (const each of state.players) {
    for (const id of each.play) {
      const source = instanceAt(state, id);
      for (const ability of activeStaticAbilitiesOf(state, source)) {
        const effect = ability.effect;
        if (effect.kind !== 'raise-lore-to-win') continue;
        if (effect.players === 'you' && source.controller !== player) continue;
        if (effect.players === 'opponents' && source.controller === player) continue;
        if (
          ability.while !== undefined &&
          !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
        ) {
          continue;
        }
        threshold += effect.amount;
      }
    }
  }
  return threshold;
}

/**
 * Whether this player's deck-top card is currently **face up** (revealed to everyone) — true
 * while any live `reveal-deck-top` static covers their deck ("Each player plays with the top
 * card of their deck face up"). Player-scoped like `inkLimitOf`, and the same discipline:
 * computed on read, `while` guards re-evaluated each time, nothing cached — so the reveal
 * ends the moment the granting card leaves play. Read by `view.ts` to name the top card
 * positionally, and by the engine's step loop to keep `knownTo` caught up as the top changes.
 */
export function deckTopIsRevealed(state: GameState, deckOwner: PlayerIndex): boolean {
  for (const player of state.players) {
    for (const id of player.play) {
      const source = instanceAt(state, id);
      if (!printsDeckTopReveal(state.catalog, source.defId)) continue;
      for (const ability of activeStaticAbilitiesOf(state, source)) {
        const effect = ability.effect;
        if (effect.kind !== 'reveal-deck-top') continue;
        if (effect.decks === 'your' && source.controller !== deckOwner) continue;
        if (effect.decks === 'opponent' && source.controller === deckOwner) continue;
        if (
          ability.while !== undefined &&
          !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
        ) {
          continue;
        }
        return true;
      }
    }
  }
  return false;
}

/**
 * Whether **any** card in play could turn a deck top face up — the cheap question asked before
 * the live one (`deckTopIsRevealed`), which no game containing no such card should ever pay
 * for. `zones.ts` asks it on every deck mutation and the engine's step loop asks it on every
 * automatic step, so it is a memoized map lookup per card in play and nothing more.
 */
export function anyDeckTopReveal(state: GameState): boolean {
  for (const player of state.players) {
    for (const id of player.play) {
      if (printsDeckTopReveal(state.catalog, instanceAt(state, id).defId)) return true;
    }
  }
  return false;
}

/**
 * Whether a printed card carries a `reveal-deck-top` static at all — memoized per catalog,
 * printed data only, so it cannot go stale. The same amortization `costs.ts`'s `costStaticsOf`
 * uses, and the same discipline: whether the static *functions* (its `while`, whose decks it
 * covers) stays a live read.
 */
const DECK_TOP_MEMO = new WeakMap<CardCatalog, Map<number, boolean>>();

// One catalog per game: pay the WeakMap hop per catalog switch, not per card scanned.
let deckTopCatalog: CardCatalog | null = null;
let deckTopByDef: Map<number, boolean> | null = null;

function printsDeckTopReveal(catalog: CardCatalog, defId: number): boolean {
  if (catalog !== deckTopCatalog) {
    let byDef = DECK_TOP_MEMO.get(catalog);
    if (byDef === undefined) {
      byDef = new Map();
      DECK_TOP_MEMO.set(catalog, byDef);
    }
    deckTopCatalog = catalog;
    deckTopByDef = byDef;
  }
  const byDef = deckTopByDef as Map<number, boolean>;
  let prints = byDef.get(defId);
  if (prints === undefined) {
    prints = staticAbilitiesOf(catalog, defId).some(
      (ability) => ability.effect.kind === 'reveal-deck-top',
    );
    byDef.set(defId, prints);
  }
  return prints;
}

/** Every in-play instance id, ascending — instance order, for the reads where order shows. */
function inPlayAscending(state: GameState): number[] {
  const ids: number[] = [];
  for (const player of state.players) for (const id of player.play) ids.push(id);
  return ids.sort((a, b) => a - b);
}

/** Whether a stored record covers this card right now — scope first, then the "while" guard. */
function modifierApplies(
  state: GameState,
  modifier: Modifier,
  card: CardInstance,
  asZone?: Zone,
): boolean {
  if (!inScope(state, modifier, card, asZone)) return false;
  if (modifier.guard === undefined) return true;
  return evalCondition(state, contextFor(modifier.source, modifier.controller, card), modifier.guard);
}

function inScope(
  state: GameState,
  modifier: Modifier,
  card: CardInstance,
  asZone?: Zone,
): boolean {
  const scope = modifier.scope;
  if (scope.kind === 'cards') {
    // A snapshot names the card itself; it still has to be in play to have characteristics —
    // unless the read named the zone it is being asked about, as a `play` restriction does.
    return card.zone === (asZone ?? 'play') && scope.cards.includes(card.id);
  }
  // A player-scoped record covers no card at all: it is read by `playerPermissionCount`.
  if (scope.kind === 'player') return false;
  return matchesLive(state, modifier.source, modifier.controller, card, scope.filter, asZone);
}

/** Whether one card in play is under one continuous static right now. */
function staticApplies(
  state: GameState,
  source: CardInstance,
  ability: StaticAbility,
  card: CardInstance,
  asZone?: Zone,
): boolean {
  if (
    ability.while !== undefined &&
    !evalCondition(state, contextFor(source.id, source.controller, source), ability.while)
  ) {
    return false;
  }
  const filter = staticFilterOf(ability.effect);
  if (filter === null) return false;
  return matchesLive(state, source.id, source.controller, card, filter, asZone);
}

/**
 * Which cards a continuous static applies to. `null` for a static that applies to no card in
 * play at all — a deck-construction rule, an alternate cost, a free-play permission and a
 * conditional Shift grant (`StaticEffect`; `costs.ts` reads all three, `grant-shift` has no
 * `filter` at all — it is self-only, ticket 109) and the player-scoped reads (`inkLimitOf`,
 * `handCountsAsInkable`, `discardCountsAsInkable`, `deckTopIsRevealed`, `canGainLore`,
 * `loreToWinOf`, `drawStepSkipped`, `challengeDamageStatOf`).
 */
function staticFilterOf(effect: StaticEffect): Selector | null {
  return effect.kind === 'deck-limit' ||
    effect.kind === 'deck-ink-rule' ||
    effect.kind === 'alternate-cost' ||
    effect.kind === 'free-play' ||
    effect.kind === 'grant-shift' ||
    effect.kind === 'extra-ink' ||
    effect.kind === 'hand-inkable' ||
    effect.kind === 'discard-inkable' ||
    effect.kind === 'restrict-lore-gain' ||
    effect.kind === 'raise-lore-to-win' ||
    effect.kind === 'reveal-deck-top' ||
    effect.kind === 'skip-draw' ||
    effect.kind === 'challenge-damage-stat'
    ? null
    : effect.filter;
}

/**
 * Selector matching for a continuous effect: the **live** matcher, so a filter reads modified
 * characteristics (CR §6.4.4). The recursion guard around every characteristic read is what
 * keeps a self-referential filter from looping.
 *
 * `asZone` rebinds the filter's zone to the one the read is about — the `play` restriction's
 * "wherever this card is being played from" (see `isRestricted`), and nothing else.
 */
function matchesLive(
  state: GameState,
  source: InstanceId,
  controller: PlayerIndex,
  card: CardInstance,
  filter: Selector,
  asZone?: Zone,
): boolean {
  const scoped = asZone === undefined ? filter : { ...filter, zone: asZone };
  return matchesSelector(state, contextFor(source, controller, card), card, scoped);
}

/**
 * The context a modifier's own IR is evaluated in: `self` is the card the effect came from
 * and `subject` is the card being read — which is what lets a static say "+{S} equal to their
 * {W}" and mean the affected card's {W}.
 */
function contextFor(
  source: InstanceId,
  controller: PlayerIndex,
  card: CardInstance,
): AbilityContext {
  return baseContext(source, controller, card.id);
}

function amountOf(
  state: GameState,
  amount: Amount,
  source: InstanceId,
  controller: PlayerIndex,
  card: CardInstance,
): number {
  if (typeof amount === 'number') return amount;
  return evalAmount(state, contextFor(source, controller, card), amount);
}

/**
 * The cycle breaker described at the top of the module. Re-entering the computation of the
 * *same* characteristic of the *same* card yields the fallback (no modifiers — the printed
 * base), which terminates and stays deterministic; anything else recurses normally.
 *
 * The set is transient: it is empty again before the read returns, so it is not state, it
 * cannot be cloned, serialized or observed, and it caches nothing. Keys are packed numbers
 * rather than strings to keep every read off the allocator — these guards sit inside the
 * hottest loops the engine has.
 */
const evaluating = new Set<number>();

/** One slot per distinct read a card supports; `guardKey` packs it beside the card's id. */
const STAT_SLOT: Record<Stat, number> = { strength: 0, willpower: 1, lore: 2 };
const KEYWORDS_SLOT = 3;
const KEYWORD_LOSS_SLOT = 4;
const ABILITIES_SLOT = 5;
const SUBTYPES_SLOT = 6;
const RESTRICT_SLOT: Record<Restriction, number> = {
  quest: 7,
  challenge: 8,
  ready: 9,
  sing: 10,
  play: 11,
  'enter-play-ready': 12,
  'be-challenged': 13,
  move: 14,
  'must-quest': 15,
};
const FLOOR_SLOT: Record<Stat, number> = { strength: 16, willpower: 17, lore: 18 };
const SUPPRESSED_SLOT = 19;

/**
 * `against` distinguishes `isRestricted` reads against different second cards (a `be-challenged`
 * challenger, a `sing` song): `0` for the reads that have none, an instance id + 2 otherwise.
 * Exact while instance ids stay below 2^20 — six orders of magnitude past any real game.
 *
 * The slot multiplier is 32 — one more bit than the original 16 slots ever needed — so
 * `FLOOR_SLOT` (ticket 115) and `SUPPRESSED_SLOT` (ticket 118) both had room without
 * renumbering `RESTRICT_SLOT`'s existing keys.
 */
function guardKey(cardId: number, slot: number, against = 0): number {
  return (cardId * 32 + slot) * 0x100000 + against;
}

/** Shared empty results — the common "no modifiers apply" read allocates nothing. */
const NO_KEYWORDS: KeywordInstance[] = [];
const NO_KEYWORD_NAMES: KeywordName[] = [];
const NO_ABILITIES: Ability[] = [];
const NO_SUBTYPES: string[] = [];
const NO_SUPPRESSED: readonly string[] = [];

// ---------------------------------------------------------------------------
// Durations — the boundaries a record does not survive (CR §6.1.13)
// ---------------------------------------------------------------------------

/** The turn-structure boundaries where durations run out. */
export type DurationBoundary =
  /** The Beginning phase of a turn, before the Ready step (CR §3.2). */
  | 'start-of-turn'
  /** The End-of-Turn phase, after its triggers have resolved (CR §3.4). */
  | 'end-of-turn';

/**
 * Drop every modifier whose duration has run out at this boundary, and prune every snapshot
 * of the cards that have since left play.
 *
 * "This turn" ends in the End-of-Turn phase; "until the start of your next turn" ends as its
 * controller's next turn begins — never at the end of the turn it was made on, which is the
 * whole difference between the two.
 */
export function expireModifiers(
  state: GameState,
  boundary: DurationBoundary = 'end-of-turn',
): void {
  const kept: Modifier[] = [];
  const refresh: InstanceId[] = [];
  for (const modifier of state.modifiers) {
    if (hasExpired(state, modifier, boundary)) {
      // A card that *loses* a trigger-derived keyword must be re-registered too, or the
      // trigger the grant installed would outlive the grant.
      if (changesTriggers(modifier)) refresh.push(...snapshotOf(modifier));
      continue;
    }
    const pruned = pruneScope(state, modifier);
    if (pruned !== null) kept.push(pruned);
  }
  state.modifiers = kept;
  if (refresh.length > 0) {
    refreshTriggers(
      state,
      refresh.filter((id) => instanceAt(state, id).zone === 'play'),
    );
  }
}

function hasExpired(state: GameState, modifier: Modifier, boundary: DurationBoundary): boolean {
  switch (modifier.duration) {
    case 'this-turn':
      return boundary === 'end-of-turn';
    case 'until-start-of-your-next-turn':
      return (
        boundary === 'start-of-turn' &&
        state.activePlayer === modifier.controller &&
        state.turn > modifier.created
      );
    // "During their next turn": it survives the end of the turn it was made on and expires at
    // the end of the following one — which is that player's turn in a two-player game.
    case 'until-end-of-their-next-turn':
      return boundary === 'end-of-turn' && state.turn > modifier.created;
    case 'permanent':
      return false;
  }
}

/**
 * Continuous effects on a card end when it leaves play, along with its damage counters
 * (CR §1.9) — a card that comes back is a new object as far as they go. A modifier that
 * snapshotted several cards keeps working on the ones still there.
 */
export function dropModifiersFor(state: GameState, id: InstanceId): void {
  const kept: Modifier[] = [];
  for (const modifier of state.modifiers) {
    const pruned = pruneScope(state, modifier, id);
    if (pruned !== null) kept.push(pruned);
  }
  state.modifiers = kept;
}

/**
 * Move every applied static from one card onto another — what **Shift** does to the effects
 * on the character it is played onto (CR §8.10: the stack is one character, and "keeps
 * effects applying to the shifted-onto character").
 *
 * Only snapshots need moving: a `filter` scope is re-matched on every read, so it follows the
 * character to its new top card by itself. Called by `stacks.ts` *before* the base leaves
 * play, so the `dropModifiersFor` that follows finds nothing left naming it.
 */
export function retargetModifiers(state: GameState, from: InstanceId, to: InstanceId): void {
  state.modifiers = state.modifiers.map((modifier) => {
    const scope = modifier.scope;
    if (scope.kind !== 'cards' || !scope.cards.includes(from)) return modifier;
    const cards = scope.cards.filter((id) => id !== from);
    if (!cards.includes(to)) cards.push(to);
    return { ...modifier, scope: { kind: 'cards', cards } };
  });
}

/** The record with departed cards removed from its snapshot, or `null` if nothing is left. */
function pruneScope(state: GameState, modifier: Modifier, leaving?: InstanceId): Modifier | null {
  const scope = modifier.scope;
  if (scope.kind !== 'cards') return modifier;
  const cards = scope.cards.filter(
    (id) => id !== leaving && instanceAt(state, id).zone === 'play',
  );
  if (cards.length === 0) return null;
  if (cards.length === scope.cards.length) return modifier;
  return { ...modifier, scope: { kind: 'cards', cards } };
}
